import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Copy, FileDown, FileText, Check } from 'lucide-react';
import { renderMarkdown } from './markdown';
import ReportChart from './charts/ReportChart';

const STROKE = 2.75;

// Build a brand-chip index from the report's rows_used so brand names
// in the narrative become clickable pills, matching how regular
// assistant messages render mentions.
function reportBrandIndex(report) {
  const rows = (report && report.rows_used) || [];
  return rows.map(r => ({ id: r.id, name: r.name, hovercard: null }));
}

// Split the report markdown around `<<chart:N>>` placeholders and
// interleave <ReportChart> components between the markdown segments.
function renderReportBody(markdown, charts, brandIndex) {
  if (!markdown) return null;
  const out = [];
  const re = /<<chart:(\d+)>>/g;
  let cursor = 0;
  let m;
  let segKey = 0;
  while ((m = re.exec(markdown)) !== null) {
    const before = markdown.slice(cursor, m.index);
    if (before.trim()) {
      out.push(
        <div key={`md-${segKey++}`} className="chat-report-md">
          {renderMarkdown(before, brandIndex)}
        </div>,
      );
    }
    const idx = parseInt(m[1], 10) - 1;
    const cfg = (charts || [])[idx];
    if (cfg) {
      out.push(<ReportChart key={`chart-${segKey++}-${idx}`} config={cfg} />);
    }
    cursor = m.index + m[0].length;
  }
  const tail = markdown.slice(cursor);
  if (tail.trim()) {
    out.push(
      <div key={`md-${segKey++}`} className="chat-report-md">
        {renderMarkdown(tail, brandIndex)}
      </div>,
    );
  }
  // If the markdown didn't include any placeholders but the report
  // still produced charts, drop them at the end so they're at least
  // visible. Same fallback used by services/reports.py for the
  // narrative-failed path.
  if (out.length && !re.test(markdown) && (charts || []).length) {
    // (Don't re-execute the regex — RegExp.exec mutates lastIndex.)
    const used = (markdown.match(/<<chart:\d+>>/g) || []).length;
    if (used === 0) {
      charts.forEach((c, idx) => {
        out.push(<ReportChart key={`chart-tail-${idx}`} config={c} />);
      });
    }
  }
  return out;
}

function ReportMessage({ report }) {
  const [copied, setCopied] = useState(false);
  const wrapRef = useRef(null);

  const brandIndex = useMemo(() => reportBrandIndex(report), [report]);
  const body = useMemo(
    () => renderReportBody(report?.markdown || '', report?.charts || [], brandIndex),
    [report, brandIndex],
  );

  const copy = useCallback(async () => {
    if (!report?.markdown) return;
    try {
      await navigator.clipboard.writeText(report.markdown);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      // Clipboard API blocked (insecure context / permissions) — fall
      // back to a hidden textarea so the operator still gets a copy.
      const ta = document.createElement('textarea');
      ta.value = report.markdown;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } finally { document.body.removeChild(ta); }
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  }, [report]);

  // PDF export piggybacks on the browser's print dialog: open it
  // scoped to the report wrapper via a temporary print stylesheet.
  // Zero new dependencies; the operator picks "Save as PDF" from the
  // standard print sheet.
  const print = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const cloned = el.cloneNode(true);
    const win = window.open('', '_blank', 'noopener,noreferrer');
    if (!win) return;
    win.document.write(`<!doctype html><html><head><title>Brand Report</title>
      <style>
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
               color: #1e293b; padding: 32px; }
        h2, h3, h4 { margin-top: 18px; }
        table { border-collapse: collapse; width: 100%; margin: 12px 0; font-size: 13px; }
        th, td { border: 1px solid #cbd5e1; padding: 6px 10px; text-align: left; }
        th { background: #f1f5f9; }
        figure.chat-report-chart { margin: 16px 0; }
        figcaption { font-size: 13px; font-weight: 600; margin-bottom: 6px; }
        .chat-report-toolbar { display: none; }
        canvas { max-width: 100% !important; height: auto !important; }
      </style>
    </head><body></body></html>`);
    win.document.body.appendChild(cloned);
    // Wait for charts (canvas) to render before triggering print.
    setTimeout(() => {
      win.print();
    }, 600);
  }, []);

  if (!report) return null;
  if (report.error) {
    return (
      <div className="chat-report chat-report-error">
        <FileText size={16} strokeWidth={STROKE} />
        <span>Report failed: {report.error}</span>
      </div>
    );
  }

  const diag = report.diagnostics || {};
  return (
    <div className="chat-report" ref={wrapRef}>
      <div className="chat-report-header">
        <div className="chat-report-title">
          <FileText size={15} strokeWidth={STROKE} />
          <span>Brand report</span>
          {diag.total_matching != null ? (
            <span className="chat-report-meta">
              {diag.row_count} of {diag.total_matching} matching brands
              {diag.charts_rendered ? ` · ${diag.charts_rendered} chart${diag.charts_rendered === 1 ? '' : 's'}` : ''}
            </span>
          ) : null}
        </div>
        <div className="chat-report-toolbar">
          <button type="button" onClick={copy} className="btn btn-secondary chat-report-action">
            {copied ? <Check size={13} strokeWidth={STROKE} /> : <Copy size={13} strokeWidth={STROKE} />}
            <span>{copied ? 'Copied' : 'Copy MD'}</span>
          </button>
          <button type="button" onClick={print} className="btn btn-secondary chat-report-action">
            <FileDown size={13} strokeWidth={STROKE} />
            <span>Print / PDF</span>
          </button>
        </div>
      </div>
      <div className="chat-report-body">{body}</div>
    </div>
  );
}

export default ReportMessage;
